import { c as _c } from "react/compiler-runtime";
import { getServicePricingForSelfSchedule } from "../helpers";
import { useIsRwgLanding } from "@/contexts/self-schedule-provider";
import { atDurationBound } from "@/lib/provider-service-duration";
import { computeBookingFeeCents } from "@/lib/service-fees";
import { computePatientFacingDuration, computePatientFacingDurationWithAddons } from "@/lib/service-utils";
import { cx } from "@/lib/tailwind-utils";
import { PublicServiceDetail } from "@/types/service";
import { PricingInfo } from "./price-display";
import { ServiceMenuItemDesktop } from "./service-menu-item-desktop";
import { ServiceMenuItemMobile } from "./service-menu-item-mobile";
export interface ServiceMenuItemProps {
  service: PublicServiceDetail;
  className?: string;
  isSelected: boolean;
  selectionCount?: number;
  maxQuantity?: number;
  disabled: boolean;
  onServiceClick: (serviceId: string) => void;
  onQuantityChange?: (serviceId: string, quantity: number) => void;
  onInfoClick?: (e: React.MouseEvent<HTMLButtonElement | SVGElement>, serviceId: string) => void;
  animationDelay?: number;
  /** When false, the duration label is hidden. Defaults to showing it. */
  showDuration?: boolean;
  /** Inline descriptions stay in normal flow, two lines with See more, for add-on drawers. */
  descriptionDisplay?: "hover" | "inline";
  /** Show a longer description for a singleton group or ungrouped menu. */
  standalone?: boolean;
  /** Shown before the price, e.g. "+" on an add-on. */
  pricePrefix?: string;
}

/**
 * Compute the minimum duration for a service, including any mandatory addons
 * (selectionMin). Price is not adjusted — the service displays its configured
 * display price regardless of addons.
 */
function computeMinDuration(service: PublicServiceDetail): number {
  const addons = service.addons ?? [];
  const selectionMin = service.selectionMin ?? 0;
  if (addons.length === 0 || selectionMin <= 0) {
    return computePatientFacingDuration([service]);
  }
  const minimumAddons = [...addons].sort((a, b) => computePatientFacingDuration([service, a.addonService]) - computePatientFacingDuration([service, b.addonService])).slice(0, selectionMin).map(addon => addon.addonService);
  return computePatientFacingDurationWithAddons(service, minimumAddons);
}
export const ServiceMenuItem = t0 => {
  const $ = _c(63);
  const {
    service,
    className,
    isSelected,
    selectionCount,
    maxQuantity,
    disabled,
    onServiceClick,
    onQuantityChange,
    onInfoClick,
    animationDelay,
    showDuration: t1,
    standalone: t2,
    descriptionDisplay: t3,
    pricePrefix
  } = t0;
  const showDuration = t1 === undefined ? true : t1;
  const standalone = t2 === undefined ? false : t2;
  const descriptionDisplay = t3 === undefined ? "hover" : t3;
  const isRwgLanding = useIsRwgLanding();
  const bookingFee = computeBookingFeeCents(service);
  const showBookingFee = !isRwgLanding && bookingFee > 0;
  const hasAddons = (service.addons?.length ?? 0) > 0;
  let t4;
  if ($[0] !== service) {
    t4 = computeMinDuration(atDurationBound(service, "min"));
    $[0] = service;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const durationMin = t4;
  let t5;
  if ($[2] !== service) {
    t5 = computeMinDuration(atDurationBound(service, "max"));
    $[2] = service;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const durationMax = t5;
  let t6;
  if ($[4] !== service) {
    t6 = getServicePricingForSelfSchedule(service);
    $[4] = service;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const pricing = t6;
  let t7;
  if ($[6] !== onInfoClick || $[7] !== service.id) {
    t7 = onInfoClick ? e => {
      onInfoClick(e, service.id);
    } : undefined;
    $[6] = onInfoClick;
    $[7] = service.id;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleInfoClick = t7;
  const t8 = service.id;
  const t9 = isSelected ? "bg-gradient-to-bl from-primary/10 via-transparent to-transparent" : disabled ? "cursor-not-allowed !opacity-40" : "hover:bg-gradient-to-r hover:from-background-secondary/60 hover:to-transparent";
  let t10;
  if ($[9] !== className || $[10] !== t9) {
    t10 = cx("group relative cursor-pointer py-4 md:py-6 transition-all duration-300", "border-b border-border/80 last:border-b-0", "motion-preset-fade-sm hover:z-10", "group-hover/list:md:opacity-50 hover:!opacity-100", t9, className);
    $[9] = className;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const t11 = !isSelected && animationDelay ? `${animationDelay}ms` : undefined;
  let t12;
  if ($[12] !== t11) {
    t12 = {
      animationDelay: t11
    };
    $[12] = t11;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== disabled || $[15] !== onServiceClick || $[16] !== service.id) {
    t13 = () => !disabled && onServiceClick(service.id);
    $[14] = disabled;
    $[15] = onServiceClick;
    $[16] = service.id;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const t14 = descriptionDisplay === "inline";
  let t15;
  if ($[18] !== bookingFee || $[19] !== disabled || $[20] !== durationMax || $[21] !== durationMin || $[22] !== handleInfoClick || $[23] !== hasAddons || $[24] !== isSelected || $[25] !== maxQuantity || $[26] !== onQuantityChange || $[27] !== pricePrefix || $[28] !== pricing || $[29] !== selectionCount || $[30] !== service || $[31] !== showBookingFee || $[32] !== showDuration || $[33] !== standalone || $[34] !== t14) {
    t15 = <ServiceMenuItemMobile standalone={standalone} collapsibleDescription={t14} service={service} isSelected={isSelected} selectionCount={selectionCount} maxQuantity={maxQuantity} disabled={disabled} onQuantityChange={onQuantityChange} showBookingFee={showBookingFee} bookingFee={bookingFee} pricing={pricing} onInfoClick={handleInfoClick} hasAddons={hasAddons} durationMin={durationMin} durationMax={durationMax} showDuration={showDuration} pricePrefix={pricePrefix} />;
    $[18] = bookingFee;
    $[19] = disabled;
    $[20] = durationMax;
    $[21] = durationMin;
    $[22] = handleInfoClick;
    $[23] = hasAddons;
    $[24] = isSelected;
    $[25] = maxQuantity;
    $[26] = onQuantityChange;
    $[27] = pricePrefix;
    $[28] = pricing;
    $[29] = selectionCount;
    $[30] = service;
    $[31] = showBookingFee;
    $[32] = showDuration;
    $[33] = standalone;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== bookingFee || $[37] !== descriptionDisplay || $[38] !== disabled || $[39] !== durationMax || $[40] !== durationMin || $[41] !== handleInfoClick || $[42] !== hasAddons || $[43] !== isSelected || $[44] !== maxQuantity || $[45] !== onQuantityChange || $[46] !== pricePrefix || $[47] !== pricing || $[48] !== selectionCount || $[49] !== service || $[50] !== showBookingFee || $[51] !== showDuration || $[52] !== standalone) {
    t16 = <ServiceMenuItemDesktop descriptionDisplay={descriptionDisplay} standalone={standalone} service={service} isSelected={isSelected} selectionCount={selectionCount} maxQuantity={maxQuantity} disabled={disabled} onQuantityChange={onQuantityChange} showBookingFee={showBookingFee} bookingFee={bookingFee} pricing={pricing} onInfoClick={handleInfoClick} hasAddons={hasAddons} durationMin={durationMin} durationMax={durationMax} showDuration={showDuration} pricePrefix={pricePrefix} />;
    $[36] = bookingFee;
    $[37] = descriptionDisplay;
    $[38] = disabled;
    $[39] = durationMax;
    $[40] = durationMin;
    $[41] = handleInfoClick;
    $[42] = hasAddons;
    $[43] = isSelected;
    $[44] = maxQuantity;
    $[45] = onQuantityChange;
    $[46] = pricePrefix;
    $[47] = pricing;
    $[48] = selectionCount;
    $[49] = service;
    $[50] = showBookingFee;
    $[51] = showDuration;
    $[52] = standalone;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  let t17;
  if ($[54] !== t15 || $[55] !== t16) {
    t17 = <div className="pl-4 pr-3">{t15}{t16}</div>;
    $[54] = t15;
    $[55] = t16;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] !== service.id || $[58] !== t10 || $[59] !== t12 || $[60] !== t13 || $[61] !== t17) {
    t18 = <div data-service-card-id={t8} className={t10} style={t12} onClick={t13}>{t17}</div>;
    $[57] = service.id;
    $[58] = t10;
    $[59] = t12;
    $[60] = t13;
    $[61] = t17;
    $[62] = t18;
  } else {
    t18 = $[62];
  }
  return t18;
};
export default ServiceMenuItem;